import { Autocomplete, Chip, FormControlLabel, Stack, Switch, TextField } from '@mui/material'
import { DangerousGoodsDefinitionDto, TemperatureUnit } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { ImdgCodes, SelectOption } from '@planning/constants'
import { useTranslate } from '@tolgee/react'
import { CustomInputAdornment } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import {
  DangerousGoodsDefinitionFormProfile,
  defaultValues,
  mapDangerousGoodsDefinitionDtoToDangerousGoodsDefinitionFormProfile,
} from '../utils/dangerousGoodsDefinitionsTableFormUtils'

interface DangerousGoodsDefinitionsTableFormProps {
  id: string
  dangerousGoodsDefinition?: DangerousGoodsDefinitionDto
  onSubmit: (formValues: DangerousGoodsDefinitionFormProfile) => Promise<void>
}

const DangerousGoodsDefinitionsTableForm = observer(
  ({ id, onSubmit, dangerousGoodsDefinition }: DangerousGoodsDefinitionsTableFormProps) => {
    const { tenantStore } = usePlanningStore()

    const defaultFormValues = {
      ...defaultValues,
      flashPointUnit: tenantStore.temperatureUnit,
    }
    const {
      getValues,
      handleSubmit,
      control,
      register,
      formState: { errors },
      reset,
    } = useForm<DangerousGoodsDefinitionFormProfile>({
      defaultValues: defaultFormValues,
    })

    const imoClassOptions: SelectOption[] = useMemo(() => {
      return ImdgCodes.map(ic => ({ label: ic, value: ic }))
    }, [])

    const { t } = useTranslate()

    useEffect(() => {
      if (dangerousGoodsDefinition)
        reset(
          mapDangerousGoodsDefinitionDtoToDangerousGoodsDefinitionFormProfile(
            dangerousGoodsDefinition,
            tenantStore.temperatureUnit,
          ),
        )
    }, [reset, dangerousGoodsDefinition, tenantStore.temperatureUnit])

    return (
      <Stack
        id={id}
        component='form'
        noValidate
        autoComplete='off'
        onSubmit={handleSubmit(onSubmit)}
        my={1}
      >
        <Stack gap={1}>
          <Stack direction='row' gap={1}>
            <TextField
              fullWidth
              label={`${t('unNumber', 'UN Number')}`}
              required
              {...register('unNumber', {
                required: t('fieldIsRequired', 'Field is required.'),
                pattern: {
                  value: /^\d{4}$/,
                  message: t('mustBe4Digits', 'Must be exactly 4 digits.'),
                },
              })}
              error={!!errors.unNumber}
              helperText={errors.unNumber?.message}
            />

            <Controller
              name='primaryImoClass'
              control={control}
              rules={{ required: t('fieldIsRequired', 'Field is required.') }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Autocomplete
                  fullWidth
                  options={imoClassOptions}
                  getOptionLabel={(option: SelectOption) => option.label}
                  value={imoClassOptions.find(option => option.value === value) || null}
                  onChange={(_, newValue) => {
                    onChange(newValue ? newValue.value : null)
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={`${t('primaryImoClass', 'Primary IMO Class')}`}
                      error={!!error}
                      helperText={error ? error.message : ''}
                      required
                      fullWidth
                    />
                  )}
                />
              )}
            />
          </Stack>

          <Controller
            name='secondaryImoClasses'
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Autocomplete
                multiple
                options={imoClassOptions}
                getOptionLabel={(option: SelectOption) => option.label}
                value={value ? imoClassOptions.filter(option => value.includes(option.value)) : []}
                onChange={(_, newValue) => {
                  onChange(newValue.map(item => item.value))
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('secondaryImoClasses', 'Secondary IMO Classes')}
                    error={!!error}
                    helperText={error ? error.message : ''}
                  />
                )}
                renderTags={tagValue =>
                  tagValue.map(option => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      onDelete={() => {
                        const newValue = value.filter(
                          (val: string | number) => val !== option.value,
                        )
                        onChange(newValue)
                      }}
                    />
                  ))
                }
              />
            )}
          />

          <TextField
            fullWidth
            label={`${t('material', 'Material')}`}
            {...register('material', {
              required: t('fieldIsRequired', 'Field is required.'),
            })}
            required
            error={!!errors.material}
            helperText={errors.material?.message}
          />

          <TextField
            fullWidth
            label={t('packingGroup', 'Packing group')}
            {...register('packingGroup')}
          />
          <Controller
            name='flashPoint'
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                type='number'
                InputProps={{
                  endAdornment: (
                    <CustomInputAdornment position='end' color='secondary'>
                      {getValues('flashPointUnit') === TemperatureUnit.Celsius ? '°C' : '°F'}
                    </CustomInputAdornment>
                  ),
                }}
                label={t('flashPoint', 'Flash point')}
                slotProps={{ htmlInput: { step: 0.1, pattern: '^-?\\d+(\\.\\d{0,2})?$' } }}
                onChange={e => {
                  const value = e.target.value
                  if (/^-?\d*(\.\d{0,2})?$/.test(value) || value === '') {
                    field.onChange(e)
                  }
                }}
              />
            )}
          />

          <TextField
            fullWidth
            label={t('handlingNotes', 'Handling notes')}
            {...register('handlingNotes')}
            multiline
            minRows={2}
            error={!!errors.handlingNotes}
            helperText={errors.handlingNotes?.message}
          />
        </Stack>
        <Controller
          name='isMarinePollutant'
          control={control}
          render={({ field }) => (
            <FormControlLabel
              label={t('marinePollutant', 'Marine pollutant')}
              control={
                <Switch
                  {...field}
                  checked={field.value}
                  onChange={event => field.onChange(event.target.checked)}
                  color='primary'
                />
              }
            />
          )}
        />
      </Stack>
    )
  },
)

export default DangerousGoodsDefinitionsTableForm
